<template>
  <div class="cfg-terminal">
    <div class="cfg-terminal__card">

      <!-- ── Cabeçalho ────────────────────────────────────────────────────── -->
      <div class="cfg-terminal__header">
        <img
          src="media/brand/logo.png"
          alt="PDV Desktop"
          class="cfg-terminal__logo"
        />
        <h1 class="cfg-terminal__title">Configuração do Terminal</h1>
        <p class="cfg-terminal__subtitle">
          Informe o UUID do terminal liberado no painel web.
        </p>
      </div>

      <!-- ── Etapa 1 – Formulário de vínculo ──────────────────────────────── -->
      <template v-if="etapa === 'form'">

        <!-- Erro geral -->
        <div v-if="erro" class="cfg-terminal__alert cfg-terminal__alert--error" role="alert">
          <i class="fas fa-times-circle"></i>
          <span>{{ erro }}</span>
        </div>

        <div class="cfg-terminal__field">
          <label class="cfg-terminal__label" for="cfg-uuid">
            UUID / Código do terminal <span class="cfg-terminal__required">*</span>
          </label>
          <input
            id="cfg-uuid"
            v-model.trim="form.uuid_terminal"
            type="text"
            class="cfg-terminal__input"
            :class="{ 'cfg-terminal__input--error': erros.uuid_terminal }"
            placeholder="Ex.: caixa-01 ou xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            autocomplete="off"
            :disabled="processando"
            @input="erros.uuid_terminal = ''; erro = ''"
          />
          <span v-if="erros.uuid_terminal" class="cfg-terminal__field-error">
            {{ erros.uuid_terminal }}
          </span>
          <span class="cfg-terminal__hint">
            Disponível em <strong>Configurações → Terminais</strong> no painel web.
          </span>
        </div>

        <div class="cfg-terminal__field">
          <label class="cfg-terminal__label" for="cfg-device-name">
            Nome deste computador
            <span class="cfg-terminal__optional">(opcional)</span>
          </label>
          <input
            id="cfg-device-name"
            v-model.trim="form.device_name"
            type="text"
            class="cfg-terminal__input"
            placeholder="Ex.: Caixa Principal"
            autocomplete="off"
            :disabled="processando"
          />
          <span class="cfg-terminal__hint">
            Facilita identificar o computador no painel web.
          </span>
        </div>

        <button
          type="button"
          class="cfg-terminal__btn"
          :disabled="processando"
          @click="vincular"
        >
          <i v-if="processando" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-plug"></i>
          {{ processando ? "Validando..." : "Validar e configurar" }}
        </button>

      </template>

      <!-- ── Etapa 2 – Sincronização inicial ──────────────────────────────── -->
      <template v-if="etapa === 'sync'">

        <div class="cfg-terminal__sync-info">
          <div class="cfg-terminal__sync-row">
            <span class="cfg-terminal__sync-label">Terminal</span>
            <span class="cfg-terminal__sync-value">{{ terminalNome }}</span>
          </div>
          <div v-if="terminalSerie" class="cfg-terminal__sync-row">
            <span class="cfg-terminal__sync-label">Série NFC-e</span>
            <span class="cfg-terminal__sync-value">{{ terminalSerie }}</span>
          </div>
        </div>

        <div class="cfg-terminal__progress-wrap">
          <div class="cfg-terminal__progress-bar">
            <div
              class="cfg-terminal__progress-fill"
              :style="{ width: progresso + '%' }"
            ></div>
          </div>
          <p class="cfg-terminal__progress-status">{{ statusSync }}</p>
        </div>

        <div
          v-if="alertaSyncParcial"
          class="cfg-terminal__alert cfg-terminal__alert--warning mt-2"
          role="alert"
        >
          <i class="fas fa-exclamation-triangle"></i>
          <span>
            Configuração fiscal concluída, porém alguns dados não foram sincronizados. Execute a sincronização novamente.
          </span>
        </div>

        <div v-if="erro" class="cfg-terminal__alert cfg-terminal__alert--error mt-2" role="alert">
          <i class="fas fa-times-circle"></i>
          <span>{{ erro }}</span>
          <div class="cfg-terminal__error-actions">
            <button
              type="button"
              class="cfg-terminal__retry-btn"
              @click="tentarSync"
            >
              Tentar novamente
            </button>
            <button
              type="button"
              class="cfg-terminal__retry-btn cfg-terminal__retry-btn--ghost"
              @click="sair"
            >
              Sair
            </button>
          </div>
        </div>

      </template>

      <!-- ── Etapa 3 – Sucesso ─────────────────────────────────────────────── -->
      <template v-if="etapa === 'ok'">
        <div class="cfg-terminal__success">
          <div class="cfg-terminal__success-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <p class="cfg-terminal__success-text">
            Terminal configurado com sucesso!
          </p>
          <p class="cfg-terminal__success-sub">Abrindo o painel...</p>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import SyncPdvDesktopService from "../services/SyncPdvDesktopService";
import TerminalConfigService from "../services/TerminalConfigService";
import { SYSTEM_VARIABLE } from "@/modules/Application";

const APP_VERSION = "1.0.0";

export default {
  name: "ConfigurarTerminal",

  data() {
    return {
      etapa: "form", // 'form' | 'sync' | 'ok'

      form: {
        uuid_terminal: "",
        device_name: "",
      },
      erros: {
        uuid_terminal: "",
      },
      erro: "",
      processando: false,

      // Dados retornados pela API após vínculo
      terminalPayload: null,

      // Sync
      progresso: 0,
      statusSync: "Aguardando...",

      // Exibição na etapa de sync
      terminalNome: "",
      terminalSerie: "",
      alertaSyncParcial: false,
    };
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }
    // Pré-preenche o nome do dispositivo via IPC
    try {
      const nome = await this.$electron.device.getDeviceName();
      if (nome && !this.form.device_name) {
        this.form.device_name = nome;
      }
    } catch (_) { /* ignora */ }
  },

  methods: {
    // ── Validação local ──────────────────────────────────────────────────
    _validar() {
      this.erros.uuid_terminal = "";
      if (!this.form.uuid_terminal) {
        this.erros.uuid_terminal = "O identificador do terminal é obrigatório.";
        return false;
      }
      return true;
    },

    // ── Passo 1: vincula terminal no backend ─────────────────────────────
    async vincular() {
      this.erro = "";
      if (!this._validar()) return;

      this.processando = true;
      try {
        const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
        if (!apiUrl) throw new Error("URL da API não configurada.");

        const token = await this.$electron.config.get("api_token");
        if (!token) throw new Error("Sessão expirada. Faça login novamente.");

        const [device_id, device_name_auto] = await Promise.all([
          this.$electron.device.getDeviceId(),
          this.$electron.device.getDeviceName(),
        ]);

        let appVersion = APP_VERSION;
        try {
          appVersion = (await this.$electron.app.getVersion()) || APP_VERSION;
        } catch (_) { /* usa padrão */ }

        const payload = {
          uuid_terminal: this.form.uuid_terminal,
          device_id,
          device_name: this.form.device_name || device_name_auto || "PDV Desktop",
          app_version: appVersion,
        };

        let resp;
        try {
          resp = await fetch(`${apiUrl}/api/offline/terminal/vincular`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          });
        } catch (_) {
          throw new Error(
            "Não foi possível validar o terminal. Verifique sua conexão e tente novamente."
          );
        }

        let json = {};
        try { json = await resp.json(); } catch (_) { /* ignora */ }

        // Mensagem do backend: aceita `msg`, `message` ou `error`
        const _backendMsg = (json.msg ?? json.message ?? json.error ?? "").trim();
        const _code       = (json.error_code ?? json.code ?? "").toString();

        // Resposta HTTP de erro OU status:false retornado com qualquer HTTP
        if (!resp.ok || json.status === false || json.status === 0) {
          // Terminal já vinculado a outro dispositivo
          if (
            resp.status === 409 ||
            _code === "TERMINAL_JA_VINCULADO"
          ) {
            throw new Error(
              "Este terminal já está vinculado a outro computador. " +
              "Para usar neste dispositivo, desvincule o terminal no painel web."
            );
          }

          // Terminal não encontrado (backend pode retornar 400 ou 404)
          if (
            resp.status === 404 ||
            _code === "TERMINAL_NAO_ENCONTRADO" ||
            /não encontrado|nao encontrado|not found/i.test(_backendMsg)
          ) {
            throw new Error(
              "Terminal não encontrado. Verifique o UUID informado."
            );
          }

          // Terminal bloqueado / inativo
          if (resp.status === 403) {
            throw new Error(
              _backendMsg || "Terminal bloqueado ou inativo. Verifique no painel web."
            );
          }

          // Qualquer outro erro: prioriza a mensagem vinda do backend
          throw new Error(
            _backendMsg || `Erro ao vincular terminal (HTTP ${resp.status}).`
          );
        }

        // Normaliza resposta: aceita { data: {...} } ou resposta direta
        const dados = json.data ?? json;
        this.terminalPayload = dados;

        // Salva o terminal localmente antes de iniciar a sync
        await this._salvarDadosLocais(dados, { device_id });

        // Remove bloqueio de operações online (pode ter chegado via redirect de desvínculo)
        TerminalConfigService.desbloquear();

        // Avança para a etapa de sincronização
        this.etapa = "sync";
        await this.tentarSync();

      } catch (e) {
        this.erro = e.message || String(e);
      } finally {
        this.processando = false;
      }
    },

    // ── Salva terminal + NFC-e config + empresa ──────────────────────────
    async _salvarDadosLocais(dados, extras = {}) {
      const terminal  = dados.terminal    ?? dados;
      const config    = dados.configuracao ?? dados.config ?? {};
      const nfce      = dados.nfce_config  ?? dados.configuracao_nfce ?? null;
      const contextoFiscal = dados.fiscal_context ?? dados.contexto_fiscal ?? dados.dados_fiscais ?? null;
      const payloadFiscalCompleto =
        contextoFiscal ??
        dados.payload_fiscal ??
        dados.dados_fiscais_completos ??
        dados;
      const emitente =
        dados.emitente ??
        contextoFiscal?.emitente ??
        dados.empresa ??
        null;
      const impressoraConfig =
        dados.impressora_config ??
        dados.impressora ??
        contextoFiscal?.impressora ??
        null;
      const idConfiguracaoNfce =
        nfce?.id_configuracao ??
        nfce?.id ??
        config.id ??
        dados.id_configuracao ??
        null;

      const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");

      await this.$electron.terminal.save({
        uuid_terminal:     terminal.uuid_terminal ?? terminal.uuid ?? this.form.uuid_terminal,
        nome:              terminal.nome ?? terminal.name ?? this.form.uuid_terminal,
        serie:             nfce?.serie ?? config.serie ?? config.serie_nfe ?? dados.serie ?? "001",
        id_terminal_venda: terminal.id_terminal_venda ?? terminal.id ?? null,
        id_configuracao:   idConfiguracaoNfce,
        proxima_venda:     nfce?.proxima_venda ?? nfce?.proximaVenda ?? config.proxima_venda ?? config.proximaVenda ?? dados.proxima_venda ?? 1,
        id_empresa:        terminal.id_empresa ?? dados.id_empresa ?? null,
        config:            { api_url: apiUrl },
      });

      await this.$electron.config.set("api_url", apiUrl);
      await this.$electron.config.set("device_id", extras.device_id || "");

      if (payloadFiscalCompleto && Object.keys(payloadFiscalCompleto).length > 0) {
        await this.$electron.config.set(
          "terminal_fiscal_payload",
          JSON.stringify(payloadFiscalCompleto)
        );
      }

      this.terminalNome  = terminal.nome ?? terminal.name ?? this.form.uuid_terminal;
      this.terminalSerie = nfce?.serie ?? config.serie ?? config.serie_nfe ?? dados.serie ?? "";

      // Salva configuração NFC-e se o backend retornou
      if (nfce && Object.keys(nfce).length > 0) {
        try {
          await TerminalConfigService.salvarConfiguracaoNfceTerminal({
            ...nfce,
            payload_json: dados,
          });
        } catch (e) {
          console.warn("[ConfigurarTerminal] nfce config não salva:", e);
          throw e;
        }
      } else {
        throw new Error(TerminalConfigService.mensagemConfiguracaoNfceAusente());
      }

      // Salva dados do emitente se retornados
      if (emitente && Object.keys(emitente).length > 0) {
        try {
          // emitente_local.id_empresa é NOT NULL — garante a partir de outras
          // origens caso o payload do emitente não traga a chave.
          if (emitente.id_empresa == null) {
            emitente.id_empresa =
              emitente.id ??
              emitente.empresa_id ??
              terminal.id_empresa ??
              dados.id_empresa ??
              dados.empresa?.id_empresa ??
              dados.empresa?.id ??
              null;
          }
          if (emitente.id_empresa == null) {
            throw new Error("id_empresa do emitente ausente no payload do contexto fiscal.");
          }
          await this.$electron.emitente.salvarEmitente(emitente);
        } catch (e) {
          console.warn("[ConfigurarTerminal] emitente não salvo:", e);
        }
      }

      if (impressoraConfig && Object.keys(impressoraConfig).length > 0) {
        try {
          await this.$electron.impressoraConfig.salvar(impressoraConfig);
        } catch (e) {
          console.warn("[ConfigurarTerminal] impressora não salva:", e);
        }
      }
    },

    // ── Passo 2: sincronização inicial ───────────────────────────────────
    async tentarSync() {
      this.erro      = "";
      this.progresso = 0;
      this.alertaSyncParcial = false;
      this.statusSync = "Configurando terminal...";

      try {
        const resultado = await SyncPdvDesktopService.sincronizacaoInicialCompleta({
          onProgress: (pct, msg) => {
            this.progresso  = pct;
            this.statusSync = msg;
          },
        });

        if (resultado?.parcial) {
          this.alertaSyncParcial = true;
        }

        this.progresso  = 100;
        this.statusSync = "Terminal configurado com sucesso.";
        this.etapa = "ok";

        if (resultado?.parcial) {
          this.$toast.warning(
            "Configuração fiscal concluída, porém alguns dados não foram sincronizados. Execute a sincronização novamente."
          );
        }

        setTimeout(() => {
          this.$router.push({ name: "pdvDesktopDashboard" });
        }, 1400);

      } catch (e) {
        if (TerminalConfigService.isCodigoConfiguracaoNfceAusente(e?.code)) {
          this.erro = TerminalConfigService.mensagemConfiguracaoNfceAusente();
        } else if (e?.code === "FISCAL_SYNC_INICIAL_FALHOU") {
          this.erro =
            "Não foi possível baixar a configuração fiscal do terminal. Verifique a internet e tente novamente.";
        } else {
          this.erro =
            "Não foi possível concluir a sincronização. " +
            "Verifique sua conexão e tente novamente.";
        }
        console.error("[ConfigurarTerminal] sync error:", e);
      }
    },

    async sair() {
      this.$router.push({ name: "pdvDesktopLogin" });
    },
  },
};
</script>

<style scoped>
/* ── Wrapper ───────────────────────────────────────────────────────────── */
.cfg-terminal {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  padding: 2rem 1rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

/* ── Card ──────────────────────────────────────────────────────────────── */
.cfg-terminal__card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 4px 28px rgba(0, 0, 0, 0.09);
  padding: 2.5rem 2rem;
  width: 100%;
  max-width: 460px;
}

/* ── Header ────────────────────────────────────────────────────────────── */
.cfg-terminal__header {
  text-align: center;
  margin-bottom: 2rem;
}

.cfg-terminal__logo {
  width: var(--brand-logo-primary-size, 180px);
  max-width: 100%;
  max-height: 100px;
  height: auto;
  object-fit: contain;
  margin-bottom: 1.25rem;
}

.cfg-terminal__title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.4rem;
  letter-spacing: -0.3px;
}

.cfg-terminal__subtitle {
  font-size: 0.88rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

/* ── Alerta de erro ────────────────────────────────────────────────────── */
.cfg-terminal__alert {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.85rem;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.25rem;
  line-height: 1.5;
}

.cfg-terminal__alert i {
  margin-top: 1px;
  flex-shrink: 0;
}

.cfg-terminal__alert--error {
  background: #fdecea;
  color: #842029;
  border: 1px solid #f5c2c7;
}

.cfg-terminal__alert--warning {
  background: #fff3cd;
  color: #664d03;
  border: 1px solid #ffe69c;
}

.mt-2 { margin-top: 0.75rem; }

/* ── Campos ────────────────────────────────────────────────────────────── */
.cfg-terminal__field {
  margin-bottom: 1.25rem;
}

.cfg-terminal__label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
}

.cfg-terminal__required {
  color: #dc3545;
  margin-left: 1px;
}

.cfg-terminal__optional {
  font-weight: 400;
  color: #9ca3af;
  font-size: 0.78rem;
}

.cfg-terminal__input {
  width: 100%;
  padding: 0.65rem 0.9rem;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.92rem;
  color: #1e293b;
  background: #fff;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s;
}

.cfg-terminal__input:focus {
  border-color: var(--primary-color, #ee7361);
  box-shadow: 0 0 0 3px rgba(238, 115, 97, 0.15);
}

.cfg-terminal__input--error {
  border-color: #dc3545;
}

.cfg-terminal__input:disabled {
  background: #f3f4f6;
  color: #9ca3af;
  cursor: not-allowed;
}

.cfg-terminal__field-error {
  display: block;
  font-size: 0.78rem;
  color: #dc3545;
  margin-top: 0.3rem;
}

.cfg-terminal__hint {
  display: block;
  font-size: 0.78rem;
  color: #9ca3af;
  margin-top: 0.35rem;
}

/* ── Botão principal ───────────────────────────────────────────────────── */
.cfg-terminal__btn {
  width: 100%;
  padding: 0.78rem;
  background: linear-gradient(135deg, var(--primary-color, #ee7361) 0%, #e05a47 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: opacity 0.18s, transform 0.12s;
  letter-spacing: 0.2px;
  margin-top: 0.5rem;
}

.cfg-terminal__btn:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}

.cfg-terminal__btn:active:not(:disabled) {
  transform: translateY(0);
}

.cfg-terminal__btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

/* ── Etapa sync: info do terminal ──────────────────────────────────────── */
.cfg-terminal__sync-info {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.cfg-terminal__sync-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0;
}

.cfg-terminal__sync-row + .cfg-terminal__sync-row {
  border-top: 1px solid #e9ecef;
}

.cfg-terminal__sync-label {
  font-size: 0.82rem;
  color: #6c757d;
  font-weight: 600;
}

.cfg-terminal__sync-value {
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 700;
}

/* ── Barra de progresso ────────────────────────────────────────────────── */
.cfg-terminal__progress-wrap {
  margin-bottom: 0.5rem;
}

.cfg-terminal__progress-bar {
  height: 8px;
  background: #e9ecef;
  border-radius: 99px;
  overflow: hidden;
}

.cfg-terminal__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--primary-color, #ee7361), #28a745);
  border-radius: 99px;
  transition: width 0.4s ease;
}

.cfg-terminal__progress-status {
  font-size: 0.82rem;
  color: #6c757d;
  margin-top: 0.5rem;
  min-height: 1.2em;
}

/* ── Botão retry ───────────────────────────────────────────────────────── */
.cfg-terminal__retry-btn {
  margin-left: auto;
  flex-shrink: 0;
  background: none;
  border: 1px solid #842029;
  color: #842029;
  border-radius: 6px;
  padding: 0.25rem 0.65rem;
  font-size: 0.78rem;
  cursor: pointer;
  white-space: nowrap;
}

.cfg-terminal__retry-btn:hover {
  background: #fdecea;
}

.cfg-terminal__error-actions {
  margin-left: auto;
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}

.cfg-terminal__retry-btn--ghost {
  border-color: #6c757d;
  color: #495057;
}

.cfg-terminal__retry-btn--ghost:hover {
  background: #f1f3f5;
}

/* ── Etapa de sucesso ──────────────────────────────────────────────────── */
.cfg-terminal__success {
  text-align: center;
  padding: 1.5rem 0 0.5rem;
}

.cfg-terminal__success-icon {
  font-size: 3rem;
  color: #28a745;
  margin-bottom: 0.75rem;
}

.cfg-terminal__success-text {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.4rem;
}

.cfg-terminal__success-sub {
  font-size: 0.88rem;
  color: #6c757d;
  margin: 0;
}
</style>
